import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Upload, Sparkles, Trash2, ArrowLeft, AlertTriangle, Save } from "lucide-react";
import { toast } from "sonner";

const UNITS = ["kg", "gm", "L", "ml", "pcs", "pack", "dozen", "bag", "box", "bundle"];

export default function BulkItemsFromPhoto() {
  const { t, user } = useApp();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [reading, setReading] = useState(false);
  const [rows, setRows] = useState([]);
  const [engine, setEngine] = useState("");
  const [saving, setSaving] = useState(false);

  if (user?.role !== "owner") {
    return <Card className="p-8 text-center text-stone-500" data-testid="bulk-items-owner-only">Owner only</Card>;
  }

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setReading(true); setRows([]);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/items/from-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      setEngine(r.data.engine);
      // Assign a stable client-side id so React key survives row deletions
      setRows((r.data.rows || []).map((row, idx) => ({ ...row, _rid: `${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 6)}` })));
      if ((r.data.rows || []).length === 0) toast.error(t("no_items_found"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("read_failed"));
    } finally { setReading(false); }
  };

  const setRow = (rid, patch) => setRows((prev) => prev.map((r) => r._rid === rid ? { ...r, ...patch } : r));
  const delRow = (rid) => setRows((prev) => prev.filter((r) => r._rid !== rid));

  const validRows = rows.filter((r) => (r.name_en || "").trim() && Number(r.selling_price) > 0);
  const invalidCount = rows.length - validRows.length;

  const commit = async () => {
    if (validRows.length === 0) return toast.error(t("no_items_found"));
    setSaving(true);
    try {
      const payload = { items: validRows.map((r) => ({
        name_en: r.name_en.trim(),
        name_gu: (r.name_gu || "").trim(),
        unit: r.unit || "pcs",
        cost_price: Number(r.cost_price) || 0,
        selling_price: Number(r.selling_price) || 0,
        stock: Number(r.stock) || 0,
        barcode: (r.barcode || "").trim(),
      })) };
      const r = await api.post("/items/bulk-create", payload);
      toast.success(t("items_created").replace("{count}", r.data.created));
      nav("/items");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  // Shared column-widths grid for md+ table-like layout (matches header row exactly)
  // [# | name_en | name_gu | unit | cost | price | stock | del]
  const MD_GRID = "md:grid md:grid-cols-[28px_minmax(0,1.4fr)_minmax(0,1.4fr)_88px_100px_100px_92px_36px] md:items-center md:gap-2";

  return (
    <div className="space-y-5 pb-24">
      <div className="flex items-center gap-3">
        <button onClick={() => nav("/items")} data-testid="bulk-back" className="h-10 w-10 rounded-lg bg-stone-100 grid place-items-center">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("items")}</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-stone-900 mt-0.5">{t("bulk_items_title")}</h1>
        </div>
      </div>

      {rows.length === 0 && !reading && (
        <Card className="p-8 rounded-2xl border-dashed border-2 border-stone-300 text-center" data-testid="bulk-upload-card">
          <Upload className="w-12 h-12 text-[#B45309] mx-auto mb-4" />
          <p className="text-stone-500 mb-4 text-sm max-w-md mx-auto">{t("bulk_items_help")}</p>
          <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,image/*,application/pdf"
                 onChange={onFile} className="hidden" data-testid="bulk-file-input" />
          <Button onClick={() => fileRef.current?.click()} data-testid="bulk-upload-btn"
                  className="h-14 px-6 rounded-xl bg-[#B45309] hover:bg-[#92400E] text-white font-semibold text-lg">
            <Upload className="w-5 h-5 mr-2" /> {t("upload_stock_list")}
          </Button>
        </Card>
      )}

      {reading && (
        <Card className="p-10 rounded-2xl text-center" data-testid="bulk-reading">
          <Sparkles className="w-8 h-8 text-[#B45309] mx-auto mb-3 animate-pulse" />
          <div className="font-heading text-lg">{t("reading")}</div>
          <div className="text-sm text-stone-500 mt-1">Gemini 3.1 Pro</div>
        </Card>
      )}

      {rows.length > 0 && !reading && (
        <>
          <Card className="p-4 rounded-2xl border-stone-200 flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("engine_used")}</div>
              <div className="font-mono text-sm font-semibold text-[#166534]" data-testid="bulk-engine">{engine}</div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("review_items")}</div>
              <div className="font-mono text-sm font-semibold" data-testid="bulk-count">{validRows.length} / {rows.length}</div>
            </div>
            {invalidCount > 0 && (
              <div className="text-amber-700 text-xs flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> {invalidCount} incomplete (name + price required)
              </div>
            )}
          </Card>

          {/* Sticky column-header (tablet+ only). Sticks below the app header (h-16 = 64px). */}
          <div className={`hidden md:block sticky top-16 z-20 -mx-2`} data-testid="bulk-sticky-header">
            <div className="mx-2 px-4 py-2 rounded-xl bg-[#FFF7ED] border border-[#B45309]/20 shadow-sm">
              <div className={MD_GRID}>
                <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500 text-center">#</div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#B45309]">{t("name_en")}</div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#B45309]">{t("name_gu")}</div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#B45309]">{t("unit")}</div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#B45309]">{t("cost_price")}</div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#B45309]">{t("selling_price")}</div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#B45309]">{t("stock")}</div>
                <div />
              </div>
            </div>
          </div>

          <div className="space-y-3 md:space-y-2">
            {rows.map((r, i) => {
              const invalid = !r.name_en?.trim() || !(Number(r.selling_price) > 0);
              const flagged = r.needs_check || invalid;
              return (
                <Card key={r._rid} data-testid={`bulk-row-${i}`}
                      className={`p-3 md:p-2.5 rounded-2xl border ${flagged ? "bg-amber-50/60 border-l-4 border-amber-500" : "border-stone-200"}`}>
                  {/* Mobile-only original text row */}
                  <div className="md:hidden mb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">#{i + 1} · original</div>
                        <div className="text-sm text-stone-700 font-gujarati mt-0.5" data-testid={`bulk-raw-${i}`}>{r.raw_text || "—"}</div>
                      </div>
                    </div>
                  </div>

                  {/* Fields — flat single-row grid at md+, stacked at mobile */}
                  <div className={MD_GRID}>
                    <div className="hidden md:block text-xs font-mono text-stone-500 text-center" data-testid={`bulk-idx-${i}`}>{i + 1}</div>

                    {/* Names */}
                    <div className="grid grid-cols-1 gap-2 md:contents mb-2 md:mb-0">
                      <Input placeholder={t("name_en")} value={r.name_en} onChange={(e) => setRow(r._rid, { name_en: e.target.value })} data-testid={`bulk-en-${i}`} className="h-10 md:h-10 rounded-lg" />
                      <Input placeholder={t("name_gu")} value={r.name_gu} onChange={(e) => setRow(r._rid, { name_gu: e.target.value })} data-testid={`bulk-gu-${i}`} className="h-10 md:h-10 rounded-lg font-gujarati" />
                    </div>

                    {/* Numeric fields */}
                    <div className="grid grid-cols-4 gap-2 md:contents">
                      <div className="md:contents">
                        <label className="md:hidden text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">{t("unit")}</label>
                        <select value={r.unit || "pcs"} onChange={(e) => setRow(r._rid, { unit: e.target.value })} data-testid={`bulk-unit-${i}`} className="h-10 w-full rounded-lg border border-stone-300 px-2 text-sm">
                          {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </div>
                      <div className="md:contents">
                        <label className="md:hidden text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">{t("cost_price")}</label>
                        <Input type="number" placeholder={t("cost_price")} value={r.cost_price ?? 0} onChange={(e) => setRow(r._rid, { cost_price: parseFloat(e.target.value) || 0 })} data-testid={`bulk-cost-${i}`} className="h-10 rounded-lg font-mono w-full" />
                      </div>
                      <div className="md:contents">
                        <label className="md:hidden text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">{t("selling_price")}</label>
                        <Input type="number" placeholder={t("selling_price")} value={r.selling_price ?? 0} onChange={(e) => setRow(r._rid, { selling_price: parseFloat(e.target.value) || 0 })} data-testid={`bulk-price-${i}`} className="h-10 rounded-lg font-mono w-full" />
                      </div>
                      <div className="md:contents">
                        <label className="md:hidden text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">{t("stock")}</label>
                        <Input type="number" placeholder={t("stock")} value={r.stock ?? 0} onChange={(e) => setRow(r._rid, { stock: parseFloat(e.target.value) || 0 })} data-testid={`bulk-stock-${i}`} className="h-10 rounded-lg font-mono w-full" />
                      </div>
                    </div>

                    {/* Delete */}
                    <button onClick={() => delRow(r._rid)} data-testid={`bulk-del-${i}`} className="hidden md:grid h-9 w-9 rounded-lg bg-red-50 place-items-center text-red-600 shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Mobile-only delete floating button */}
                  <button onClick={() => delRow(r._rid)} data-testid={`bulk-del-mobile-${i}`}
                          className="md:hidden mt-2 w-full h-9 rounded-lg bg-red-50 text-red-600 text-xs font-semibold flex items-center justify-center gap-1">
                    <Trash2 className="w-3.5 h-3.5" /> Remove row
                  </button>
                </Card>
              );
            })}
          </div>

          <div className="fixed bottom-16 md:bottom-0 left-0 right-0 md:left-auto md:right-6 md:bottom-6 md:w-96 bg-white border-t md:border md:rounded-2xl border-stone-200 p-3 sm:p-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-30">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] sm:text-xs uppercase tracking-widest text-stone-500">{t("review_items")}</div>
                <div className="font-mono text-2xl sm:text-3xl font-bold text-stone-900 truncate" data-testid="bulk-valid-count">{validRows.length}</div>
              </div>
              <Button onClick={commit} disabled={saving || validRows.length === 0} data-testid="bulk-commit"
                      className="h-12 sm:h-14 px-3 sm:px-5 rounded-xl bg-[#166534] hover:bg-[#14532D] disabled:bg-stone-300 text-white font-semibold text-sm sm:text-base whitespace-nowrap">
                <Save className="w-4 h-4 mr-1 sm:mr-2" /> {t("create_items").replace("{count}", validRows.length)}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
